import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Tag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback blog posts
const fallbackPosts = [
  {
    id: '1',
    title: 'Nuova Normativa UNI 11900:2023 per Installatori Linee Vita',
    slug: 'normativa-uni-11900-2023',
    excerpt: 'La nuova norma UNI 11900:2023 introduce requisiti aggiornati per la formazione degli installatori di sistemi anticaduta.',
    image_url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    category: 'normative',
    created_at: '2024-01-15'
  },
  {
    id: '2',
    title: 'Manutenzione Linee Vita: Quando e Come Effettuarla',
    slug: 'manutenzione-linee-vita-guida',
    excerpt: 'Guida completa alla revisione periodica dei sistemi anticaduta secondo il D.Lgs. 81/08.',
    image_url: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800',
    category: 'guide',
    created_at: '2024-01-10'
  },
  {
    id: '3',
    title: 'Parapetti Anticaduta: Soluzione Permanente per la Sicurezza',
    slug: 'parapetti-anticaduta-sicurezza',
    excerpt: 'I parapetti rappresentano la soluzione di protezione collettiva più efficace per i lavori in quota.',
    image_url: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800',
    category: 'prodotti',
    created_at: '2024-01-05'
  }
];

const categoryColors = {
  normative: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  guide: 'bg-green-500/20 text-green-400 border-green-500/30',
  prodotti: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  news: 'bg-revo-orange/20 text-revo-orange border-revo-orange/30'
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      if (response.data && response.data.length > 0) {
        setPosts(response.data);
      } else {
        setPosts(fallbackPosts);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts(fallbackPosts);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-revo-bg pt-20 min-h-screen">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              News & Aggiornamenti
            </span>
            <h1 className="font-barlow text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mt-4 mb-6">
              Blog<br />
              <span className="text-gray-500">RevoQuota</span>
            </h1>
            <p className="font-manrope text-lg text-gray-300 leading-relaxed">
              Resta aggiornato sulle ultime novità in materia di sicurezza, 
              normative e soluzioni anticaduta.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-revo-paper border border-revo-border">
                  <div className="aspect-video bg-revo-subtle animate-pulse" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-revo-subtle animate-pulse rounded w-1/3" />
                    <div className="h-6 bg-revo-subtle animate-pulse rounded" />
                    <div className="h-4 bg-revo-subtle animate-pulse rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-revo-paper border border-revo-border hover:border-revo-orange/30 transition-colors"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative aspect-video overflow-hidden">
                      <img 
                        src={post.image_url || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800'} 
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-revo-bg/80 to-transparent" />
                    </div>

                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <span className={`px-2 py-1 text-xs font-manrope uppercase tracking-wider border ${
                          categoryColors[post.category] || categoryColors.news
                        }`}>
                          {post.category}
                        </span>
                        <div className="flex items-center gap-1 text-gray-500 text-xs">
                          <Calendar className="w-3 h-3" />
                          <span className="font-manrope">{formatDate(post.created_at)}</span>
                        </div>
                      </div>

                      <h2 className="font-barlow text-xl font-bold uppercase tracking-wide text-white mb-3 group-hover:text-revo-orange transition-colors line-clamp-2">
                        {post.title}
                      </h2>

                      <p className="font-manrope text-gray-400 text-sm leading-relaxed line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center gap-2 text-revo-orange font-manrope text-sm group-hover:gap-4 transition-all">
                        <span>Leggi</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
